import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CareGiver, ValidationErrors } from "../../types/customer";
import { CustomerDetailsForm } from "../CustomerDetailsForm";
import { CustomerStatusBar } from "../CustomerStatusBar";
import { AddressesSection } from "../AddressesSection";
import { CommunicationPreferences } from "../CommunicationPreferences";
import { RelationshipSection } from "../RelationshipSection";
import { CustomerAddress } from "../../types/address";
import { AddressLookupModal } from "../AddressLookupModal";
import { AddressModal } from "../AddressModal";
import { format, isValid, parseISO } from "date-fns";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";

interface NextOfKinDialogProps {
  open: boolean;
  onClose: () => void;
  caregiver?: CareGiver;
  onSubmit: (caregiver: CareGiver | Omit<CareGiver, "relationshipId">) => void;
}

const defaultCaregiver: Omit<CareGiver, "relationshipId"> = {
  externalId: undefined,
  isActive: true,
  isPrimary: false,
  relationshipType: "",
  title: "",
  firstName: "",
  lastName: "",
  dateOfBirth: undefined,
  patientGenderCode: "X",
  isDeceased: false,
  deceasedDate: null,
  email: "",
  phones: [],
  isCommercialClient: false,
  communicationPreference: {
    allowEmail: null,
    allowSms: null,
    allowPhone: null,
    allowLetter: null,
    reason: 1,
  },
  addresses: [],
  hasMatch: false,
};

export const NextOfKinDialog: React.FC<NextOfKinDialogProps> = ({
  open,
  onClose,
  caregiver,
  onSubmit,
}) => {
  // Form state
  const [formData, setFormData] = useState<Omit<CareGiver, "relationshipId">>(
    () => (caregiver ? { ...caregiver } : { ...defaultCaregiver })
  );
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  // Form field states
  const [dobInputTempValue, setDobInputTempValue] = useState<Date | null>(null);
  const [deceasedInputTempValue, setDeceasedInputTempValue] =
    useState<Date | null>(null);
  const [hasNoEmailChecked, setHasNoEmailChecked] = useState(false);
  const [hasNoPhoneChecked, setHasNoPhoneChecked] = useState(false);
  const [phoneValidationErrors, setPhoneValidationErrors] = useState<
    (string | undefined)[]
  >([]);

  // Address modal states
  const [isAddressLookupModalOpen, setIsAddressLookupModalOpen] =
    useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<
    CustomerAddress | undefined
  >(undefined);

  // Reset form when dialog is opened/closed
  React.useEffect(() => {
    if (!open) {
      setFormData(defaultCaregiver);
      setValidationErrors({});
      setDobInputTempValue(null);
      setDeceasedInputTempValue(null);
      setHasNoEmailChecked(false);
      setHasNoPhoneChecked(false);
      setPhoneValidationErrors([]);
    }
  }, [open]);

  // Update form data when caregiver changes
  React.useEffect(() => {
    if (caregiver) {
      setFormData(caregiver);
      // Set other form states
      setHasNoEmailChecked(!caregiver.email);
      setHasNoPhoneChecked(caregiver.phones.length === 0);
    }
  }, [caregiver]);

  // Effects for date fields
  React.useEffect(() => {
    if (formData.dateOfBirth) {
      const parsed = parseISO(formData.dateOfBirth);
      setDobInputTempValue(isValid(parsed) ? parsed : null);
    }
  }, [formData.dateOfBirth]);

  React.useEffect(() => {
    if (formData.deceasedDate) {
      const parsed = parseISO(formData.deceasedDate);
      setDeceasedInputTempValue(isValid(parsed) ? parsed : null);
    }
  }, [formData.deceasedDate]);

  // Handlers
  const handleInputChange = (e: any) => {
    const target = e.target;
    const name = target.name;
    const isCheckbox = target.type === "checkbox";
    const value = isCheckbox
      ? !!(target as { checked?: boolean }).checked
      : target.value;

    if (!name) return;

    if (name === "isDeceased" && value === false) {
      setDeceasedInputTempValue(null);
      setFormData((prev) => ({
        ...prev,
        isDeceased: false,
        deceasedDate: null,
      }));
    } else if (name.startsWith("communicationPreference.")) {
      const [, field] = name.split(".");
      let parsedValue: boolean | null = null;
      if (value === "true" || value === true) parsedValue = true;
      else if (value === "false" || value === false) parsedValue = false;
      else parsedValue = null;
      setFormData((prev) => ({
        ...prev,
        communicationPreference: {
          ...prev.communicationPreference,
          [field]: parsedValue,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDobAccept = (newValue: Date | null) => {
    if (newValue instanceof Date && isValid(newValue)) {
      handleInputChange({
        target: { name: "dateOfBirth", value: format(newValue, "yyyy-MM-dd") },
      });
    }
  };

  const handleDeceasedDateAccept = (newValue: Date | null) => {
    if (newValue instanceof Date && isValid(newValue)) {
      handleInputChange({
        target: { name: "deceasedDate", value: format(newValue, "yyyy-MM-dd") },
      });
    }
  };

  const handleAddAddress = () => {
    setIsAddressLookupModalOpen(true);
  };

  const handleAddressSelect = (address: CustomerAddress) => {
    setFormData((prev) => ({
      ...prev,
      addresses: [...prev.addresses, address],
    }));
    setIsAddressLookupModalOpen(false);
  };

  const handleEditAddress = (address: CustomerAddress) => {
    setSelectedAddress(address);
    setIsAddressModalOpen(true);
  };

  const handleRemoveAddress = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateAddress = (
    index: number,
    updates: Partial<CustomerAddress>
  ) => {
    setFormData((prev) => {
      const newAddresses = [...prev.addresses];
      if (updates.isPrimary) {
        newAddresses.forEach((addr) => {
          addr.isPrimary = false;
        });
      }
      newAddresses[index] = { ...newAddresses[index], ...updates };
      return { ...prev, addresses: newAddresses };
    });
  };

  const handleHasNoEmailCheckedChanged = (event: any) => {
    const isChecked = event.target.checked;
    setHasNoEmailChecked(isChecked);

    if (isChecked) {
      handleInputChange({ target: { name: "email", value: "" } });
      setValidationErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handleConfirm = () => {
    // Validate form data here
    // Validate the form
    const validationErrors: ValidationErrors = {};

    // Required fields validation
    if (!formData.firstName.trim())
      validationErrors.firstName = "First name is required";
    if (!formData.lastName.trim())
      validationErrors.lastName = "Last name is required";
    if (!formData.relationshipType.trim())
      validationErrors.relationshipType = "Relationship type is required";

    // Phone validation
    const phoneErrors = formData.phones.map((phone) => {
      if (!phone.number) return undefined;
      const parsed = parsePhoneNumberFromString(phone.number, "GB");
      return parsed && parsed.isValid() ? undefined : "Invalid phone number";
    });
    setPhoneValidationErrors(phoneErrors);

    // Email validation if provided
    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      validationErrors.email = "Invalid email format";
    }

    setValidationErrors(validationErrors);

    // Return if there are any validation errors
    if (
      Object.keys(validationErrors).length > 0 ||
      phoneErrors.some((e) => !!e)
    ) {
      return;
    }

    // Submit the form data
    onSubmit(formData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {caregiver
            ? `Edit ${caregiver.firstName} ${caregiver.lastName}`
            : "New Next of Kin"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <CustomerStatusBar
          formData={{
            ...formData,
            isCommercialClient: false, // Force this to be false
          }}
          validationErrors={validationErrors}
          isNew={!caregiver}
          deceasedInputTempValue={deceasedInputTempValue}
          onInputChange={(e) => {
            // Prevent changes to isCommercialClient
            if (e.target.name === "isCommercialClient") return;
            handleInputChange(e);
          }}
          onDeceasedDateChange={setDeceasedInputTempValue}
          onDeceasedDateAccept={handleDeceasedDateAccept}
          onDeceasedDateClose={() => {
            if (
              deceasedInputTempValue instanceof Date &&
              isValid(deceasedInputTempValue)
            ) {
              handleDeceasedDateAccept(deceasedInputTempValue);
            }
          }}
          onSystemSelect={() => {}}
          onCheckSyncStatus={async () => ({ hearing: false, optics: false })}
          onForceSync={async () => {}}
          disableCustomerToggle={true}
          hideDesktopIcon={true}
          showSharedSpace={false}
          onShowSharedSpaceChange={() => {}}
        />

        <Box display="flex" gap={3}>
          <Box flex={1} minWidth={0}>
            <CustomerDetailsForm
              formData={formData}
              validationErrors={validationErrors}
              dobInputTempValue={dobInputTempValue}
              careHomes={[]}
              loadingCareHomes={false}
              hasNoEmailChecked={hasNoEmailChecked}
              hasNoPhoneChecked={hasNoPhoneChecked}
              phoneValidationErrors={phoneValidationErrors}
              onInputChange={handleInputChange}
              onDobChange={setDobInputTempValue}
              onDobAccept={handleDobAccept}
              onDobClose={() => {
                if (
                  dobInputTempValue instanceof Date &&
                  isValid(dobInputTempValue)
                ) {
                  handleDobAccept(dobInputTempValue);
                }
              }}
              onCareHomeChange={() => {}}
              onCareHomeInputChange={() => {}}
              onHasNoEmailCheckedChange={handleHasNoEmailCheckedChanged}
              onPhonesChange={(phones) =>
                setFormData((prev) => ({ ...prev, phones }))
              }
              onPhoneValidationErrorsChange={setPhoneValidationErrors}
              onHasNoPhoneCheckedChange={setHasNoPhoneChecked}
            />
          </Box>

          <Box
            flex={1}
            minWidth={0}
            display="flex"
            flexDirection="column"
            gap={3}
          >
            <Box flex={2}>
              <AddressesSection
                addresses={formData.addresses}
                validationErrors={validationErrors}
                onAddAddress={handleAddAddress}
                onEditAddress={handleEditAddress}
                onRemoveAddress={handleRemoveAddress}
                onUpdateAddress={handleUpdateAddress}
              />
            </Box>
            <Box flex={1}>
              <RelationshipSection
                relationshipType={formData.relationshipType}
                onChange={handleInputChange}
                error={validationErrors.relationshipType}
              />
            </Box>
          </Box>
        </Box>

        <CommunicationPreferences
          communicationPreference={formData.communicationPreference}
          validationErrors={validationErrors}
          isDeceased={formData.isDeceased}
          onInputChange={handleInputChange}
          onReasonChange={(reason) => {
            setFormData((prev) => ({
              ...prev,
              communicationPreference: {
                ...prev.communicationPreference,
                reason,
              },
            }));
          }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          {caregiver ? "Edit Next of Kin" : "Add Next of Kin"}
        </Button>
      </DialogActions>

      {/* Modals */}
      <AddressLookupModal
        isVisible={isAddressLookupModalOpen}
        onClose={() => setIsAddressLookupModalOpen(false)}
        onAddressSelect={handleAddressSelect}
        onAddAddress={() => {
          setIsAddressLookupModalOpen(false);
          setIsAddressModalOpen(true);
        }}
      />

      <AddressModal
        isVisible={isAddressModalOpen}
        onClose={() => {
          setSelectedAddress(undefined);
          setIsAddressModalOpen(false);
        }}
        address={selectedAddress}
        onSave={(address) => {
          setFormData((prev) => {
            const index = prev.addresses.findIndex(
              (a) => a.externalId === address.externalId
            );
            if (index === -1) {
              return { ...prev, addresses: [...prev.addresses, address] };
            }
            const updatedAddresses = [...prev.addresses];
            updatedAddresses[index] = address;
            return { ...prev, addresses: updatedAddresses };
          });
          setSelectedAddress(undefined);
          setIsAddressModalOpen(false);
        }}
      />
    </Dialog>
  );
};
